import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import React, { useState } from 'react';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, FormInput } from '../components';
import { CompanyOptionalFields } from '../components/CompanyOptionalFields';
import { InvitationLinkSection } from '../components/InvitationLinkSection';
import { adminApi, orgNumberApi } from '../services/api';
import { apiErrorCode } from '../utils/apiError';
import { isValidOrgNumber } from '../utils/orgNumber';
import { normalizeVat } from '../utils/vat';
import { withTestOrgNumber, companyIdentifierMessages, prefillVatFromOrgNumber, sanitizeOrgNumberInput, sanitizeVatInput } from '../utils/companyIdentifiers';
import type { CompanyCreateData, Company, InvitationDetails } from '../types';

const emptyCompany: CompanyCreateData = {
  companyName: '', organizationNumber: '', email: '', vatNumber: '', address: '', city: '', postalCode: '',
  phone: '', website: '', country: '', companyPresentation: '', specialties: [], languages: [],
  geographicArea: '', hourlyRate: '', responseTime: '', leadTime: '', experienceYears: 0, isPartner: false
};

export function AdminCompanyCreate() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<CompanyCreateData>(emptyCompany);
  const [tab, setTab] = useState<'overview' | 'practical' | 'settings'>('overview');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ company: Company; invitation: InvitationDetails } | null>(null);
  const [orgTaken, setOrgTaken] = useState(false);
  const change = (patch: Partial<CompanyCreateData>) => setData(previous => ({ ...previous, ...patch }));

  // Organisation and VAT number: sanitised, prefilled and validated like the contractor onboarding.
  const changeOrgNumber = (value: string) => {
    const organizationNumber = sanitizeOrgNumberInput(value);
    setOrgTaken(false);
    setData(previous => ({ ...previous, organizationNumber, vatNumber: prefillVatFromOrgNumber(previous.vatNumber, organizationNumber) }));
  };
  // Best-effort on blur; the API still rejects a duplicate on submit (ORG_NUMBER_ALREADY_EXISTS).
  const checkOrgAvailability = async () => {
    const value = data.organizationNumber || '';
    if (!isValidOrgNumber(value)) return;
    try {
      const { available } = await orgNumberApi.checkAvailability(value);
      setOrgTaken(!available);
    } catch { /* ignore network errors on blur */ }
  };
  const identifiers = companyIdentifierMessages(t, {
    organizationNumber: data.organizationNumber || '',
    vatNumber: data.vatNumber || '',
    organizationNumberTaken: orgTaken
  });

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    if (!data.companyName.trim() || !data.email.trim() || !event.currentTarget.checkValidity()) {
      setTab('overview');
      setError(t('companySetup.invalidFields'));
      return;
    }
    const blocking = identifiers.organizationNumberError || identifiers.vatNumberError;
    if (blocking) {
      setTab('overview'); setError(blocking); return;
    }
    setBusy(true);
    try {
      setResult(await adminApi.createCompany({ ...data, email: data.email.trim(),
        vatNumber: normalizeVat(data.vatNumber),
        specialties: data.specialties?.map(item => item.trim()).filter(Boolean),
        languages: data.languages?.map(item => item.trim()).filter(Boolean)
      }));
    } catch (failure) {
      const code = apiErrorCode(failure);
      setError(t(code === 'ORG_NUMBER_ALREADY_EXISTS' ? 'companySetup.orgExists' : code === 'EMAIL_ALREADY_EXISTS' ? 'companySetup.emailExists' : 'companySetup.createError'));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!result || busy) return;
    setBusy(true); setError('');
    try {
      const invitation = await adminApi.resendCompanyInvitation(result.invitation.id);
      setResult({ ...result, invitation });
    } catch { setError(t('companySetup.resendError')); }
    finally { setBusy(false); }
  };

  return <DashboardLayout>
    <div className="company-setup">
      <Link to="/admin/company-list" className="d-inline-flex align-items-center mb-3">{t('companySetup.back')}</Link>
      <h1 className="h4 mb-2">{t('companySetup.title')}</h1>
      <p className="text-muted mb-4">{t('companySetup.description')}</p>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {result ? <Card>
        <h2 className="h5">{t('companySetup.created', { name: result.company.companyName })}</h2>
        <div className={`alert ${result.invitation.emailStatus === 'sent' ? 'alert-success' : 'alert-warning'}`} role="status">
          {t(result.invitation.emailStatus === 'sent' ? 'companySetup.sent' : 'companySetup.notSent', { email: result.invitation.contactEmail })}
        </div>
        <InvitationLinkSection invitation={result.invitation} personalText={t('companySetup.inviteLink.personal')}
          testId="company-setup-invitation-link" deliveryTestId="company-setup-delivery" />
        {result.invitation.expiresAt && <p>{t('companySetup.expires', { date: new Intl.DateTimeFormat(i18n.language).format(new Date(result.invitation.expiresAt)) })}</p>}
        <div className="d-flex flex-column flex-sm-row flex-wrap gap-2">
          <Link className="btn btn-primary" to={`/admin/company-list/${result.company.id}`}>{t('companySetup.viewCompany')}</Link>
          <button className="btn btn-outline-primary" type="button" disabled={busy} onClick={resend}>{t('companySetup.resend')}</button>
          <Link className="btn btn-outline-secondary" to="/admin/company-invitations">{t('admin.nav.companyInvitations')}</Link>
        </div>
      </Card> : <Card>
        <form onSubmit={submit} noValidate>
          <div className="nav nav-tabs flex-wrap mb-4" aria-label={t('companySetup.sections')}>
            {(['overview', 'practical', 'settings'] as const).map(section => <button key={section} type="button"
              className={`nav-link ${tab === section ? 'active' : ''}`} aria-pressed={tab === section} onClick={() => setTab(section)}>
              {t(`companySetup.tabs.${section}`)}
            </button>)}
          </div>
          <fieldset disabled={busy}>
            <div hidden={tab !== 'overview'}>
              <p className="text-muted">{t('companySetup.requiredHelp')}</p>
              <div className="row">
                {([{ name: 'companyName', max: 255 }, { name: 'organizationNumber', max: 40 }, { name: 'vatNumber', max: 80 },
                  { name: 'address', max: 255 }, { name: 'postalCode', max: 30 }, { name: 'city', max: 120 }] as const).map(({ name, max }) => <div className="col-12 col-md-6" key={name}>
                  {name === 'address' ? <AddressAutocomplete name={name} label={t('onboarding.company.address')}
                    value={data.address} maxLength={max} disabled={busy} onAddressSelect={change}
                    onChange={event => change({ address: event.target.value })} /> :
                    <FormInput name={name} label={t(`onboarding.company.${name}`)} value={data[name]} maxLength={max}
                      required={name === 'companyName'}
                      placeholder={name === 'organizationNumber' ? t('auth.bankid.organizationNumberExample') : name === 'vatNumber' ? t('auth.bankid.vatExample') : undefined}
                      error={name === 'organizationNumber' ? identifiers.organizationNumberError : name === 'vatNumber' ? identifiers.vatNumberError : undefined}
                      onBlur={name === 'organizationNumber' ? checkOrgAvailability : undefined}
                      onChange={event => name === 'organizationNumber' ? changeOrgNumber(event.target.value)
                        : change({ [name]: name === 'vatNumber' ? sanitizeVatInput(event.target.value) : event.target.value })}>
                      {name === 'organizationNumber' && <TestOrgNumberButton disabled={busy} onGenerated={value => { setOrgTaken(false); setData(previous => withTestOrgNumber(previous, value)); }} />}
                      {name === 'vatNumber' && identifiers.vatNumberWarning && <div className="text-warning small mt-1" data-testid="company-setup-vat-warning">
                        <i className="feather-alert-triangle me-1" aria-hidden="true"></i>{identifiers.vatNumberWarning}
                      </div>}
                    </FormInput>}
                </div>)}
              </div>
              <CompanyOptionalFields value={data} onChange={change} section="overview" emailRequired />
            </div>
            <div hidden={tab !== 'practical'}><CompanyOptionalFields value={data} onChange={change} section="practical" /></div>
            <div hidden={tab !== 'settings'}>
              <label className="form-label" htmlFor="isPartner">{t('adminCompanyView.isPartner')}</label>
              <select id="isPartner" className="form-select mb-3" value={String(data.isPartner)} onChange={event => change({ isPartner: event.target.value === 'true' })}>
                <option value="false">{t('adminCompanyView.partnerNo')}</option>
                <option value="true">{t('adminCompanyView.partnerYes')}</option>
              </select>
              <p className="text-muted">{t('companySetup.verificationHelp')}</p>
            </div>
          </fieldset>
          <div className="d-flex flex-column flex-sm-row gap-2 mt-4">
            <button type="submit" className="btn btn-primary" disabled={busy}>{t(busy ? 'companySetup.saving' : 'companySetup.submit')}</button>
            <Link className="btn btn-outline-secondary" to="/admin/company-list">{t('common.cancel')}</Link>
          </div>
        </form>
      </Card>}
    </div>
  </DashboardLayout>;
}
